/**
 * Two optional remark plugins for syntax Prism's renderer supports and GFM doesn't: `==mark==` and
 * `[[kbd]]`. Off by default, because a stray `==` in model output or `[[wiki link]]` in a note
 * would otherwise turn into markup; pass them in `remarkPlugins` where the source is yours.
 *
 * Both match inside a single text node, so the marked span is plain text: `==**bold**==` is not
 * recognised.
 */
import type { PhrasingContent, Root } from 'mdast'
import { visit } from 'unist-util-visit'

function wrapMatches(tree: Root, pattern: RegExp, tagName: string) {
  visit(tree, 'text', (node, index, parent) => {
    if (!parent || index === undefined) return

    const parts: Array<PhrasingContent> = []
    let last = 0
    for (const match of node.value.matchAll(pattern)) {
      const start = match.index ?? 0
      if (start > last) parts.push({ type: 'text', value: node.value.slice(last, start) })
      parts.push({ type: 'emphasis', data: { hName: tagName }, children: [{ type: 'text', value: match[1] }] })
      last = start + match[0].length
    }
    if (last === 0) return

    if (last < node.value.length) parts.push({ type: 'text', value: node.value.slice(last) })
    parent.children.splice(index, 1, ...parts)
    // Past what was just inserted: the marked text is not scanned again.
    return index + parts.length
  })
}

/** `==text==` becomes `<mark>`. */
export function remarkMark() {
  return (tree: Root) => wrapMatches(tree, /==([^=\n]+)==/g, 'mark')
}

/** `[[Cmd]]` becomes `<kbd>`. */
export function remarkKbd() {
  return (tree: Root) => wrapMatches(tree, /\[\[([^\]\n]+)\]\]/g, 'kbd')
}
